import { initializeApp, getApps, getApp } from 'firebase/app';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  initializeAuth,
  getAuth,
  onAuthStateChanged as onAuthStateChangedWeb,
  // @ts-ignore - getReactNativePersistence solo existe en builds RN del SDK
  getReactNativePersistence,
  type Auth,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// === SDK NATIVO de Firebase (solo para AUTH) ===========================
// El SDK WEB de Firebase Auth (`firebase/auth`) en React Native + Hermes
// no logra ejecutar las requests HTTP a identitytoolkit.googleapis.com
// (confirmado en logcat: ningún log de FirebaseAuth durante intentos de
// login, timeout 15s sin error code real). Migramos AUTH a
// `@react-native-firebase/auth` que usa código Java/Kotlin nativo y resuelve
// el problema definitivamente. Firestore, Functions, Storage siguen con SDK
// web porque no presentan el problema.
//
// Usamos la API MODULAR (`getAuth(getApp())`) en lugar de la namespaced
// (`auth()`) — la namespaced está deprecated desde v22 y dispara warnings
// en runtime. La modular es la única recomendada para v24+.
import { getApp as getRNFirebaseApp } from '@react-native-firebase/app';
import {
  getAuth as getRNFirebaseAuth,
  onAuthStateChanged as onAuthStateChangedNativo,
} from '@react-native-firebase/auth';
// =======================================================================

// FIX H3/C4 — Configurar Firestore ANTES de que ANY otro módulo importe
// nativeFirestore() y dispare la primera lectura/escritura. `settings()` es
// inmutable después de la primera operación. Como `firestoreService.ts`
// también importa `@react-native-firebase/firestore` en su top-level y
// llama nativeFirestore() en sus métodos, este bloque DEBE ejecutarse al
// evaluar este archivo, y este archivo DEBE ser importado antes de
// firestoreService en el árbol de dependencias (lo asegura `import { db }
// from './firebaseConfig'` en firestoreService.ts).
if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const rnfFirestore = require('@react-native-firebase/firestore').default;
    rnfFirestore().settings({
      persistence: true,
      cacheSizeBytes: rnfFirestore.CACHE_SIZE_UNLIMITED,
    });
  } catch (e) {
    console.warn('[firebaseConfig] Firestore settings() falló:', (e as any)?.message);
  }

  // App Check (modo vigilancia). Va aquí, junto al arranque nativo, para que
  // salga antes de la primera petición a Firebase. No bloquea ni lanza: ver
  // services/appCheck.ts para por qué NUNCA se exige todavía.
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require('./appCheck').activarAppCheck();
  } catch (e) {
    console.warn('[firebaseConfig] App Check no arrancó:', (e as any)?.message);
  }
}

// Configuración Firebase (web SDK — usada solo para firestore/functions/storage).
// El SDK nativo lee google-services.json directamente y no necesita esto.
const firebaseConfig = {
  apiKey: "AIzaSyBzSgMwf4mCy1ot6XeiDKPzCFa9d-v8TuQ",
  authDomain: "gosafely-3e0e7.firebaseapp.com",
  projectId: "gosafely-3e0e7",
  storageBucket: "gosafely-3e0e7.firebasestorage.app",
  messagingSenderId: "298062601077",
  appId: "1:298062601077:web:e32c105facd2894890513c",
  measurementId: "G-3L5QC6JR81"
};

// Ensure we don't initialize more than once (fast refresh safe)
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

// AUTH WEB SDK — SOLO se instancia en `Platform.OS === 'web'`.
// En nativo exportamos un Proxy que ESTALLA LOUD si alguien intenta
// invocar `auth.algo()` por accidente. Antes este export era "híbrido" y
// se inicializaba con initializeAuth() en nativo, lo que causaba dos
// instancias de Auth viviendo en paralelo (web + nativa) y métodos del
// SDK web que se colgaban contra identitytoolkit.googleapis.com sin error
// claro. Mejor estallar al instante que esperar timeout 15 s.
let _auth: Auth;
if (Platform.OS === 'web') {
  _auth = getAuth(app);
} else {
  _auth = new Proxy(
    {},
    {
      get(_target, prop) {
        // Symbol.toPrimitive, then, etc. — silenciar para que JS engine no rompa.
        if (typeof prop === 'symbol') return undefined;
        throw new Error(
          `[firebaseConfig] El SDK web (firebase/auth) NO debe usarse en nativo. ` +
            `Llamada bloqueada: auth.${String(prop)}. ` +
            `Usa getNativeAuth() o authBridge en su lugar.`
        );
      },
    }
  ) as unknown as Auth;
}

export const auth = _auth; // web-only en runtime; throw guard en nativo

/**
 * Devuelve la instancia nativa de Firebase Auth para usar la API modular.
 *
 * Uso:
 *   import { getNativeAuth } from '../services/firebaseConfig';
 *   import { signInWithEmailAndPassword, onAuthStateChanged } from '@react-native-firebase/auth';
 *   const a = getNativeAuth();
 *   await signInWithEmailAndPassword(a, email, password);
 *   onAuthStateChanged(a, (user) => { ... });
 *
 * AUDIT FIX B-2 — Antes había también un export `authNative` para la API
 * namespaced legacy (deprecated en RNF v22+). Lo eliminé: cero callers en
 * el codebase. La única ruta soportada es la modular vía `getNativeAuth()`.
 */
export function getNativeAuth() {
  return getRNFirebaseAuth(getRNFirebaseApp());
}

/**
 * El usuario con sesión abierta, mirando el SDK que corresponde a la plataforma.
 *
 * Los sitios que solo QUIEREN LEER quién está logueado deben usar esto, no
 * `getNativeAuth().currentUser`: en web esa llamada lanza
 * "No Firebase App '[DEFAULT]' has been created" porque no existe la app
 * nativa. Para OPERAR (login, signOut, custom token) sigue haciendo falta el
 * SDK correcto de cada plataforma — esto es solo para consultar.
 */
export function usuarioActual(): {
  uid: string;
  email: string | null;
  emailVerified: boolean;
} | null {
  const u: any = Platform.OS === 'web' ? _auth.currentUser : getNativeAuth().currentUser;
  if (!u) return null;
  return { uid: u.uid, email: u.email ?? null, emailVerified: !!u.emailVerified };
}

/**
 * Observador de sesión que funciona en las DOS plataformas.
 *
 * POR QUÉ EXISTE — LA WEB ARRANCABA EN BLANCO
 * `_layout.tsx` y `map.tsx` llamaban a `getNativeAuth()` dentro de un
 * `useEffect` sin mirar la plataforma. En Android eso está bien: el SDK nativo
 * se auto-inicializa con `google-services.json`. En WEB no existe esa app
 * nativa, así que `getRNFirebaseApp()` lanzaba
 *
 *     No Firebase App '[DEFAULT]' has been created
 *
 * al montar el layout raíz. React desmontaba el árbol entero y la web quedaba
 * en **pantalla completamente en blanco** — el mismo síntoma que Google Play
 * describió como "your app does not open or load".
 *
 * Aquí se elige el SDK correcto según dónde corre: el web usa la instancia
 * `auth` del SDK web (que sí está inicializada, línea de arriba), el móvil usa
 * la nativa. El objeto `user` que reciben ambos trae `uid`, `email` y
 * `emailVerified`, que es todo lo que consumen los llamadores.
 */
export function observarSesion(
  cb: (
    user: {
      uid: string;
      email: string | null;
      emailVerified: boolean;
      phoneNumber: string | null;
    } | null
  ) => void
): () => void {
  if (Platform.OS === 'web') {
    return onAuthStateChangedWeb(_auth, (u) =>
      cb(
        u
          ? {
              uid: u.uid,
              email: u.email,
              emailVerified: u.emailVerified,
              phoneNumber: u.phoneNumber,
            }
          : null
      )
    );
  }
  return onAuthStateChangedNativo(getNativeAuth(), (u: any) =>
    cb(
      u
        ? {
            uid: u.uid,
            email: u.email ?? null,
            emailVerified: !!u.emailVerified,
            phoneNumber: u.phoneNumber ?? null,
          }
        : null
    )
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Google Sign-In nativo — configure UNA vez al import del módulo.
// El plugin @react-native-google-signin lee el SHA-1 + Android OAuth client
// del google-services.json automáticamente. La línea `webClientId` conecta
// el Web OAuth client (necesario para que Firebase Auth acepte el idToken
// que devuelve Google).
//
// Web Client ID:
//   1) Firebase Console → Project Settings → General →
//      Tu app web → "Web SDK configuration" → "Web client ID"
//   2) O Google Cloud Console → APIs & Services → Credentials →
//      OAuth 2.0 Client IDs → tipo "Web application"
//
// ⚠️ POR QUÉ HAY UN VALOR DE RESPALDO ESCRITO AQUÍ:
// Antes esto dependía SOLO de la variable de entorno, y si faltaba se saltaba
// el configure() en silencio. Resultado real, visto en dos móviles distintos:
//   "Google Sign-In falló — apiClient is null - call configure() first"
// La causa era de compilación, no de código: `.env` está en .gitignore, así que
// nunca viajaba a las compilaciones en la nube de EAS y la variable llegaba
// vacía. Todos los binarios generados en la nube salieron con el login de
// Google muerto — incluido el que revisó Google Play.
//
// El Web Client ID **no es un secreto**: es un identificador público que va
// incrustado en cualquier app cliente y viaja en cada petición a Google (lo
// privado es el client *secret*, que aquí no se usa). Por eso dejarlo escrito
// es seguro, y convierte un fallo total del login en algo imposible.
// La variable de entorno sigue teniendo prioridad, para poder cambiarlo sin
// tocar código.
// ─────────────────────────────────────────────────────────────────────────
const GOOGLE_WEB_CLIENT_ID_RESPALDO =
  '298062601077-e5u3l7bevsd7fbd1c5ecc9hakiq7sgme.apps.googleusercontent.com';

if (Platform.OS !== 'web') {
  try {
    const { GoogleSignin } = require('@react-native-google-signin/google-signin');
    const webClientId =
      (process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ?? '').trim() ||
      GOOGLE_WEB_CLIENT_ID_RESPALDO;
    GoogleSignin.configure({
      webClientId,
      offlineAccess: true,
    });
  } catch (e) {
    // Lib no instalada o no autolinkada — el handler mostrará mensaje claro.
    console.warn('[firebaseConfig] no se pudo configurar Google Sign-In:', e);
  }
}

export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

// Web: enableIndexedDbPersistence perezoso. El nativo ya fue configurado
// arriba con settings() ANTES de cualquier operación.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  (async () => {
    try {
      const { enableIndexedDbPersistence } = await import('firebase/firestore');
      await enableIndexedDbPersistence(db);
    } catch (e: any) {
      if (e?.code !== 'failed-precondition' && e?.code !== 'unimplemented') {
        console.warn('[firebaseConfig] IndexedDB persistence falló:', e?.message);
      }
    }
  })();
}

export default app;
